import { getAccessToken, hasKhovaSession, refreshSession } from './authSession';
import { getBaseUrl } from './config';

export class ApiError extends Error {
  status: number;
  code: string;

  constructor(status: number, code: string) {
    super(code);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
  }
}

interface ApiFetchOptions {
  // Auth endpoints answer 401 for bad credentials; refreshing there would be wrong.
  shouldRefreshOnUnauthorized?: boolean;
}

export const apiUrl = (path: string): string => `${getBaseUrl()}${path.startsWith('/') ? path : `/${path}`}`;

const send = async (path: string, init: RequestInit): Promise<Response> => {
  const token = await getAccessToken();
  if (!token) {
    return fetch(apiUrl(path), { ...init, credentials: 'include' });
  }
  const headers = new Headers(init.headers);
  if (!headers.has('authorization')) {
    headers.set('authorization', `Bearer ${token}`);
  }
  return fetch(apiUrl(path), { ...init, headers, credentials: 'include' });
};

/**
 * Every khova-api request goes through here. Web authenticates with the session cookies
 * (`credentials: 'include'`); native adds `Authorization: Bearer` from secure storage. A 401
 * on a Khova session triggers one refresh (shared by concurrent callers) and one retry.
 */
export const apiFetch = async (
  path: string,
  init: RequestInit = {},
  { shouldRefreshOnUnauthorized = true }: ApiFetchOptions = {},
): Promise<Response> => {
  const res = await send(path, init);
  if (res.status !== 401 || !shouldRefreshOnUnauthorized || !hasKhovaSession()) {
    return res;
  }
  const isRefreshed = await refreshSession();
  if (!isRefreshed) {
    return res;
  }
  return send(path, init);
};

const readErrorCode = async (res: Response): Promise<string> => {
  const body = (await res.json().catch(() => ({}))) as { error?: unknown; data?: { error?: unknown } };
  const code = body.error ?? body.data?.error;
  return typeof code === 'string' && code ? code : res.statusText || `http-${res.status}`;
};

// The standalone endpoints wrap their payload in `{ data }`; the newsfeed ones mirror the
// original `{ pagination, data }` pages. Only a body whose single key is `data` is an envelope.
const isEnvelope = (body: unknown): body is { data: unknown } =>
  typeof body === 'object' && body !== null && Object.keys(body).length === 1 && 'data' in body;

/**
 * JSON request against khova-api. Unwraps the `{ data }` envelope, returns `undefined` for
 * 204, and throws `ApiError` carrying the API error code.
 */
export const apiJson = async <T>(path: string, init: RequestInit = {}, options?: ApiFetchOptions): Promise<T> => {
  const headers = new Headers(init.headers);
  if (init.body && typeof init.body === 'string' && !headers.has('content-type')) {
    headers.set('content-type', 'application/json');
  }
  const res = await apiFetch(path, { ...init, headers }, options);
  if (!res.ok) {
    throw new ApiError(res.status, await readErrorCode(res));
  }
  if (res.status === 204) {
    return undefined as T;
  }
  const body: unknown = await res.json().catch(() => null);
  return (isEnvelope(body) ? body.data : body) as T;
};
